/**
 * Browser-side transcription client (client-only pivot, Phase 3).
 *
 * The whole pipeline runs in the user's browser with their own key:
 *
 *   file ≤ 24 MB  → uploaded as-is (any format OpenAI accepts)
 *   file > 24 MB  → duration preflight (cap check before the multi-GB
 *                   decode) → Web Audio decode → 16 kHz mono → silence-
 *                   aligned chunks → Ogg-Opus via WebCodecs (~30-min
 *                   chunks, ~7 MB each) or PCM16 WAV fallback (~10-min
 *                   chunks) → sequential requests
 *
 * whisper-1 + verbose_json is used deliberately: it is the transcription
 * model that returns segment timestamps, which the transcript UI needs.
 * Requests go through the OpenAI SDK client (user key + base URL): the SDK
 * retries 408/409/429/5xx and connection errors with backoff and honors
 * Retry-After, which a hand-rolled fixed backoff would not.
 */
import { getOpenAIKey } from "@/lib/client/openai-key";
import { getOpenAIClient } from "@/lib/openai/client";
import { APIError, APIUserAbortError } from "openai/core/error";
import { decodeToMono16k, AudioDecodeError } from "@/lib/client/audio/decode";
import {
  DEFAULT_CHUNK_SECONDS,
  planChunks,
  refineChunkBoundaries,
} from "@/lib/client/audio/chunk";
import { encodeWavPcm16Mono } from "@/lib/client/audio/wav";
import { encodeOpusOggMono, isOpusEncodeSupported } from "@/lib/client/audio/opus";
import { probeDurationSec } from "@/lib/client/audio/probe";
import type { TranscriptSegment } from "@/lib/types/domain";

/** Direct-upload threshold; OpenAI rejects request bodies over 25 MB. */
export const MAX_DIRECT_UPLOAD_BYTES = 24 * 1024 * 1024;

/** v1 duration cap — decode memory grows linearly with duration. */
export const MAX_DURATION_SEC = 2 * 60 * 60;

const TRANSCRIBE_MODEL = "whisper-1";

/** Compressed chunks are small (~7 MB at 32 kbps), so fewer, longer chunks:
 *  fewer boundaries (the transcription-quality risk) and fewer requests.
 *  WAV stays at DEFAULT_CHUNK_SECONDS to fit the 25 MB body limit. */
const OPUS_CHUNK_SECONDS = 1_800;

export type TranscriptionFailureKind =
  | "no-key"
  | "unauthorized"
  | "no-permission"
  | "no-credit"
  | "bad-audio"
  | "too-long"
  | "network"
  | "aborted";

export class TranscriptionError extends Error {
  readonly kind: TranscriptionFailureKind;

  constructor(kind: TranscriptionFailureKind, message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "TranscriptionError";
    this.kind = kind;
  }
}

export interface TranscribeAudioOptions {
  signal?: AbortSignal;
  onProgress?: (doneChunks: number, totalChunks: number) => void;
  /**
   * Optional user-supplied steering context (names, acronyms, jargon).
   * Passed as Whisper's `prompt` so unusual terms are spelled correctly.
   */
  prompt?: string;
}

/**
 * Whisper's prompt field holds ~224 tokens (~900 chars). Budget: user
 * context first (vocabulary guidance), then continuation tail from the
 * previous chunk (cross-chunk coherence) — same split the pre-pivot
 * server used (lib/workflows/transcribe, removed in the client pivot).
 */
const USER_CONTEXT_MAX_CHARS = 200;
const CONTINUATION_MAX_CHARS = 500;

/** Trim to the budget without cutting a word in half. */
function truncateAtWord(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  const cut = text.slice(0, maxChars);
  return /\s/.test(cut) ? cut.replace(/\s+\S*$/, "") : cut;
}

/**
 * Tail of the previous chunk's transcript for Whisper continuity:
 * last ≤500 chars, dropping a leading partial word, preferring to start
 * at the final sentence boundary when that keeps most of the context.
 */
function continuationTail(previousText: string): string {
  let context = previousText.slice(-CONTINUATION_MAX_CHARS).trim();
  if (previousText.length > CONTINUATION_MAX_CHARS) {
    const spaceIndex = context.indexOf(" ");
    if (spaceIndex > 0 && spaceIndex < 50) {
      context = context.slice(spaceIndex + 1);
    }
  }
  const sentencePattern = /[.!?]\s+\S/g;
  let sentenceStart = -1;
  for (const match of context.matchAll(sentencePattern)) {
    sentenceStart = match.index + 2;
  }
  if (sentenceStart > 0 && sentenceStart < context.length * 0.5) {
    context = context.slice(sentenceStart);
  }
  return context.trim();
}

/** Combined Whisper prompt; undefined when there is nothing to send. */
export function buildWhisperPrompt(
  userContext?: string,
  previousChunkText?: string,
): string | undefined {
  const parts: string[] = [];
  const context = userContext?.trim();
  if (context) {
    parts.push(truncateAtWord(context, USER_CONTEXT_MAX_CHARS));
  }
  const previous = previousChunkText?.trim();
  if (previous) {
    const tail = continuationTail(previous);
    if (tail) parts.push(tail);
  }
  return parts.length > 0 ? parts.join(" ") : undefined;
}

/** verbose_json segment shape (the fields we consume). */
interface RawSegment {
  start: number;
  end: number;
  text: string;
}

interface ChunkResult {
  offsetSec: number;
  segments: RawSegment[];
}

/** Merge per-chunk segments into the session's transcript shape. */
export function mergeChunkResults(chunks: ChunkResult[]): TranscriptSegment[] {
  const merged: TranscriptSegment[] = [];
  for (const chunk of chunks) {
    for (const segment of chunk.segments) {
      const text = segment.text.trim();
      if (!text) continue;
      merged.push({
        id: `seg-${merged.length + 1}`,
        text,
        startMs: Math.round((chunk.offsetSec + segment.start) * 1000),
        endMs: Math.round((chunk.offsetSec + segment.end) * 1000),
      });
    }
  }
  return merged;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new TranscriptionError("aborted", "Transcription cancelled.");
  }
}

/** Map an SDK failure (thrown after its own retries) to the flow's kinds. */
function toTranscriptionError(error: unknown, signal?: AbortSignal): TranscriptionError {
  if (error instanceof APIUserAbortError || signal?.aborted) {
    return new TranscriptionError("aborted", "Transcription cancelled.", { cause: error });
  }
  if (error instanceof APIError) {
    if (error.status === 401 || error.status === 403) {
      // A restricted key can pass the /models validation (which tolerates
      // insufficient_permissions) yet still lack the transcription scope.
      // Re-entering that key changes nothing — its permissions must change —
      // so it gets its own kind instead of the "re-check your key" advice.
      if (error.code === "insufficient_permissions") {
        return new TranscriptionError(
          "no-permission",
          "The key lacks permission for transcription. Edit its permissions in the OpenAI dashboard.",
          { cause: error },
        );
      }
      return new TranscriptionError(
        "unauthorized",
        "The endpoint rejected your key. Re-check it in the key settings.",
        { cause: error },
      );
    }
    if (error.status === 400 || error.status === 415) {
      return new TranscriptionError(
        "bad-audio",
        "The endpoint could not process this audio file.",
        { cause: error },
      );
    }
    // Exhausted credits arrive as 429 insufficient_quota on EVERY request —
    // a billing problem, not a transient rate limit. Retrying or re-entering
    // the key changes nothing — the key's billing must change — so it gets
    // its own kind instead of the "check your connection" advice. Some
    // OpenAI-compatible bodies carry the marker only in `type` (code null),
    // so both fields count; plain rate limits use other codes/types.
    if (
      error.status === 429 &&
      (error.code === "insufficient_quota" || error.type === "insufficient_quota")
    ) {
      return new TranscriptionError(
        "no-credit",
        "The key's account is out of credit. Add credits or billing in the OpenAI dashboard.",
        { cause: error },
      );
    }
  }
  return new TranscriptionError(
    "network",
    "Could not reach the transcription endpoint after several attempts.",
    { cause: error },
  );
}

/** One verbose_json transcription request through the SDK. */
export async function transcribeBlob(
  blob: Blob,
  filename: string,
  signal?: AbortSignal,
  prompt?: string,
): Promise<RawSegment[]> {
  if (!getOpenAIKey()) {
    throw new TranscriptionError("no-key", "No OpenAI key is set.");
  }

  let response;
  try {
    response = await getOpenAIClient().audio.transcriptions.create(
      {
        file: new File([blob], filename, { type: blob.type }),
        model: TRANSCRIBE_MODEL,
        response_format: "verbose_json",
        ...(prompt ? { prompt } : {}),
      },
      { signal },
    );
  } catch (error) {
    throw toTranscriptionError(error, signal);
  }

  if (Array.isArray(response.segments) && response.segments.length > 0) {
    return response.segments.map(({ start, end, text }) => ({ start, end, text }));
  }
  // Defensive: an endpoint that ignores verbose_json — or pairs an empty
  // segments array with real text — still yields the transcription.
  if (typeof response.text === "string" && response.text.trim()) {
    return [{ start: 0, end: 0, text: response.text }];
  }
  return [];
}

interface ChunkEncoding {
  extension: string;
  chunkSeconds: number;
  encode: (samples: Float32Array<ArrayBuffer>, sampleRate: number) => Blob | Promise<Blob>;
}

async function resolveChunkEncoding(sampleRate: number): Promise<ChunkEncoding> {
  if (await isOpusEncodeSupported(sampleRate)) {
    return { extension: "ogg", chunkSeconds: OPUS_CHUNK_SECONDS, encode: encodeOpusOggMono };
  }
  return { extension: "wav", chunkSeconds: DEFAULT_CHUNK_SECONDS, encode: encodeWavPcm16Mono };
}

/**
 * Transcribe one audio file end-to-end in the browser.
 * Progress is reported per completed chunk (the small-file fast path has
 * exactly one chunk).
 */
export async function transcribeAudio(
  file: File,
  options: TranscribeAudioOptions = {},
): Promise<TranscriptSegment[]> {
  const { signal, onProgress, prompt } = options;

  if (file.size <= MAX_DIRECT_UPLOAD_BYTES) {
    onProgress?.(0, 1);
    const segments = await transcribeBlob(
      file,
      file.name || "audio",
      signal,
      buildWhisperPrompt(prompt),
    );
    onProgress?.(1, 1);
    return mergeChunkResults([{ offsetSec: 0, segments }]);
  }

  // Metadata-only duration preflight: reject over-cap files before
  // decodeAudioData inflates them to PCM (multi-GB for hours of audio).
  // null = unknown → fall through; the post-decode check is the backstop.
  const probedDurationSec = await probeDurationSec(file);
  if (probedDurationSec !== null && probedDurationSec > MAX_DURATION_SEC) {
    throw new TranscriptionError(
      "too-long",
      "Recordings longer than 2 hours are not supported yet.",
    );
  }
  throwIfAborted(signal);

  let decoded;
  try {
    decoded = await decodeToMono16k(file);
  } catch (error) {
    if (error instanceof AudioDecodeError) {
      throw new TranscriptionError("bad-audio", error.message, { cause: error });
    }
    throw error;
  }
  throwIfAborted(signal);

  if (decoded.durationSec > MAX_DURATION_SEC) {
    throw new TranscriptionError(
      "too-long",
      "Recordings longer than 2 hours are not supported yet.",
    );
  }

  const encoding = await resolveChunkEncoding(decoded.sampleRate);
  const plans = refineChunkBoundaries(
    planChunks(decoded.samples.length, decoded.sampleRate, encoding.chunkSeconds),
    decoded.samples,
    decoded.sampleRate,
  );
  onProgress?.(0, plans.length);

  const results: ChunkResult[] = [];
  let previousChunkText = "";
  for (let i = 0; i < plans.length; i++) {
    const plan = plans[i];
    const chunkSamples = decoded.samples.subarray(
      plan.startSample,
      plan.startSample + plan.sampleCount,
    );
    let chunkBlob: Blob;
    try {
      chunkBlob = await encoding.encode(chunkSamples, decoded.sampleRate);
    } catch (error) {
      // The support probe test-encodes at selection time, so this is a true
      // runtime anomaly, not a missing feature.
      throw new TranscriptionError("bad-audio", "Could not encode this audio for upload.", {
        cause: error,
      });
    }
    throwIfAborted(signal);
    const segments = await transcribeBlob(
      chunkBlob,
      `chunk-${i + 1}.${encoding.extension}`,
      signal,
      buildWhisperPrompt(prompt, previousChunkText || undefined),
    );
    results.push({ offsetSec: plan.offsetSec, segments });
    // Roll context forward for the next chunk; keep the previous tail when
    // a chunk comes back empty (silence) rather than dropping continuity.
    const chunkText = segments.map((s) => s.text).join(" ").trim();
    if (chunkText) previousChunkText = chunkText;
    onProgress?.(i + 1, plans.length);
  }

  return mergeChunkResults(results);
}
